import { useState, useEffect, useRef } from 'react';
import { FileText, FormInput, Square, ExternalLink, Play, CheckSquare, Loader2, Clock, TrendingUp } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

interface SearchResult {
  id: string;
  type: 'page' | 'form' | 'content' | 'roadmap' | 'external_link';
  title: string;
  description: string | null;
  snippet: string;
  pageId: string | null;
  pagePath: string[];
  createdBy: string;
  createdAt: string;
  metadata: {
    submissionCount?: number;
    url?: string;
    roadmapState?: string;
  };
  relevanceScore: number;
}

interface QuickLink {
  id: string;
  title: string;
  visitCount?: number;
}

interface SearchDropdownProps {
  query: string;
  results: SearchResult[];
  totalCount: number;
  loading: boolean;
  onClose: () => void;
  onResultClick: (result: SearchResult) => void;
  onViewAll: () => void;
  selectedType: string;
  onTypeChange: (type: string) => void;
  includeDeleted: boolean;
  onIncludeDeletedChange: (include: boolean) => void;
  recentSearches: string[];
  quickLinks: QuickLink[];
  onRecentSearchClick: (query: string) => void;
  onQuickLinkClick: (pageId: string) => void;
}

// Icon mapping for result types
const getIconForType = (type: string, roadmapState?: string) => {
  switch (type) {
    case 'page':
      return FileText;
    case 'form':
      return FormInput;
    case 'content':
      return FileText;
    case 'roadmap':
      if (roadmapState === 'in_progress') return Play;
      if (roadmapState === 'complete') return CheckSquare;
      return Square;
    case 'external_link':
      return ExternalLink;
    default:
      return FileText;
  }
};

// Highlight matching text in title
const highlightText = (text: string, query: string) => {
  if (!query) return text;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, i) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-100 dark:bg-yellow-900/30 font-semibold">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export default function SearchDropdown({
  query,
  results,
  totalCount,
  loading,
  onClose,
  onResultClick,
  onViewAll,
  selectedType,
  onTypeChange,
  includeDeleted,
  onIncludeDeletedChange,
  recentSearches,
  quickLinks,
  onRecentSearchClick,
  onQuickLinkClick,
}: SearchDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Show empty state when query is less than 3 characters
  const showEmptyState = query.length < 3;
  
  // Determine what to display
  const displayResults = results.filter(r => {
    if (selectedType === 'all') return true;
    return r.type === selectedType;
  });
  
  const hasMore = displayResults.length > 5;
  
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        // Also check if the click is on the search input (to avoid closing when clicking the input)
        const target = e.target as HTMLElement;
        if (!target.closest('input[type="text"]')) {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  // Keyboard navigation (including Escape to close)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (!showEmptyState && displayResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, Math.min(displayResults.length - 1, 9)));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (displayResults[selectedIndex]) {
            onResultClick(displayResults[selectedIndex]);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [displayResults, selectedIndex, onResultClick, showEmptyState, onClose]);
  
  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results, selectedType]);

  // Render empty state with recent searches and quick links
  if (showEmptyState) {
    return (
      <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-2 bg-background rounded-lg shadow-lg border border-border z-50 overflow-hidden">
        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
              <Clock className="h-4 w-4" />
              <span>Recent Searches</span>
            </div>
            <div className="space-y-1">
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => onRecentSearchClick(search)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Quick Links */}
        {quickLinks.length > 0 && (
          <div className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
              <TrendingUp className="h-4 w-4" />
              <span>Quick Links</span>
            </div>
            <div className="space-y-1">
              {quickLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => onQuickLinkClick(link.id)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors flex items-center gap-2"
                >
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm truncate">{link.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty state when no recent searches or quick links */}
        {recentSearches.length === 0 && quickLinks.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            <p className="text-sm">Start typing to search...</p>
            <p className="text-xs mt-2">Search across pages, forms, content, and more</p>
          </div>
        )}
      </div>
    );
  }
  
  // Render search results (existing code continues below)
  return (
    <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-2 bg-background rounded-lg shadow-lg border border-border z-50 overflow-hidden">
      {/* Header with filters */}
      <div className="p-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <select
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Types</option>
            <option value="page">Pages</option>
            <option value="form">Forms</option>
            <option value="content">Content</option>
            <option value="roadmap">Roadmaps</option>
            <option value="external_link">External Links</option>
          </select>
          
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => onIncludeDeletedChange(e.target.checked)}
              className="rounded border-border"
            />
            <span className="text-muted-foreground">Include deleted</span>
          </label>
        </div>
      </div>

      {/* Results */}
      <div ref={resultsRef} className="overflow-y-auto max-h-[350px]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : results.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <p className="font-medium">No results found for "{query}"</p>
            <p className="text-sm mt-2">Try different keywords or check your spelling</p>
          </div>
        ) : (
          <>
            {displayResults.slice(0, 5).map((result, index) => {
              const Icon = getIconForType(result.type, result.metadata.roadmapState);
              const isSelected = index === selectedIndex;
              
              return (
                <button
                  key={result.id}
                  onClick={() => onResultClick(result)}
                  className={`w-full p-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0 ${
                    isSelected ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">
                        {highlightText(result.title, query)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span className="capitalize">{result.type.replace('_', ' ')}</span>
                        {result.pagePath.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="truncate">{result.pagePath.join(' > ')}</span>
                          </>
                        )}
                        {result.metadata.submissionCount !== undefined && (
                          <>
                            <span>•</span>
                            <span>{result.metadata.submissionCount} submissions</span>
                          </>
                        )}
                      </div>
                      {result.snippet && (
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {result.snippet}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            
            {hasMore && (
              <div className="p-2 text-center text-sm text-muted-foreground bg-muted/30">
                ↓ Scroll for more results...
              </div>
            )}
            
            {displayResults.slice(5, 10).map((result, index) => {
              const Icon = getIconForType(result.type, result.metadata.roadmapState);
              const actualIndex = index + 5;
              const isSelected = actualIndex === selectedIndex;
              
              return (
                <button
                  key={result.id}
                  onClick={() => onResultClick(result)}
                  className={`w-full p-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0 ${
                    isSelected ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground truncate">
                        {highlightText(result.title, query)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span className="capitalize">{result.type.replace('_', ' ')}</span>
                        {result.pagePath.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="truncate">{result.pagePath.join(' > ')}</span>
                          </>
                        )}
                      </div>
                      {result.snippet && (
                        <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {result.snippet}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </>
        )}
      </div>

      {/* View All Footer */}
      {!loading && results.length > 0 && (
        <div className="p-3 border-t border-border bg-muted/30">
          <button
            onClick={onViewAll}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            View all {totalCount} results →
          </button>
        </div>
      )}
    </div>
  );
}
